'use strict';

import { trimString, toNonNegativeInt } from '../core/bambuserFormatters';
import {
    bambuserProductIdToPid,
    bambuserReferenceToPid,
} from '../core/bambuserSharedState';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function extractPidFromUrl (url) {
    var value = trimString(url);
    if (!value) {
        return null;
    }

    var pidMatch = value.match(/[?&]pid=([^&#]+)/i);
    if (!pidMatch || !pidMatch[1]) {
        return null;
    }

    try {
        return trimString(decodeURIComponent(pidMatch[1]));
    } catch (e) {
        return trimString(pidMatch[1]);
    }
}

export function cleanProductReference (reference) {
    var value = trimString(reference);
    if (!value) {
        return null;
    }

    if (value.indexOf('tpl:') === 0) {
        return null;
    }

    if (value.indexOf('pid:') === 0) {
        return trimString(value.substring(4));
    }

    return value;
}

export function getRawProductReference (source) {
    if (!source || typeof source !== 'object') {
        return null;
    }

    return (
        trimString(source.sku) ||
        trimString(source.ref) ||
        trimString(source.productReference) ||
        null
    );
}

export function getMappedPidByReference (reference) {
    var key = trimString(reference);
    if (!key) {
        return null;
    }

    return bambuserReferenceToPid[key] || null;
}

export function extractPid (source) {
    if (!source || typeof source !== 'object') {
        return null;
    }

    return (
        cleanProductReference(source.pid) ||
        extractPidFromUrl(source.url) ||
        cleanProductReference(source.sku) ||
        cleanProductReference(source.productReference) ||
        cleanProductReference(source.ref) ||
        cleanProductReference(source.id)
    );
}

// ---------------------------------------------------------------------------
// Color-group PID helpers
// ---------------------------------------------------------------------------

export function isColorGroupPid (pid) {
    var normalized = trimString(pid);
    if (!normalized) {
        return false;
    }

    return /_color_/i.test(normalized);
}

export function getBasePidFromColorPid (pid) {
    var normalized = trimString(pid);
    if (!normalized || !isColorGroupPid(normalized)) {
        return null;
    }

    return trimString(normalized.split(/_color_/i)[0]);
}

export function getColorCodeFromPid (pid) {
    var value = trimString(pid);
    if (!value) {
        return null;
    }

    var match = value.match(/_color_([^_]+)/i);
    return match && match[1] ? trimString(match[1]) : null;
}

export function getHydrationPidCandidates (pid) {
    var candidates = [];
    var normalized = trimString(pid);

    if (normalized) {
        candidates.push(normalized);
    }

    var basePid = getBasePidFromColorPid(normalized);
    if (basePid && candidates.indexOf(basePid) === -1) {
        candidates.push(basePid);
    }

    return candidates;
}

// ---------------------------------------------------------------------------
// Event PID resolution
// ---------------------------------------------------------------------------

export function getEventPid (event) {
    var eventObject = event && typeof event === 'object' ? event : null;
    if (!eventObject) {
        return null;
    }

    var item = eventObject.item || null;
    var product = eventObject.product || null;

    var selectedSize =
        item && Array.isArray(item.sizes)
            ? item.sizes.filter(function (size) {
                return (
                    size &&
                      (size.selected === true || size.isSelected === true)
                );
            })[0]
            : null;

    var selectedVariation =
        item && Array.isArray(item.variations)
            ? item.variations.filter(function (variation) {
                return (
                    variation &&
                      (variation.selected === true ||
                          variation.isSelected === true)
                );
            })[0]
            : null;

    var mappedPid =
        (eventObject.productId &&
            bambuserProductIdToPid[eventObject.productId]) ||
        (eventObject.id &&
            bambuserProductIdToPid[eventObject.id]) ||
        (item && item.id && bambuserProductIdToPid[item.id]) ||
        (product && product.id && bambuserProductIdToPid[product.id]) ||
        null;

    var referenceMappedPid =
        getMappedPidByReference(getRawProductReference(eventObject)) ||
        getMappedPidByReference(getRawProductReference(item)) ||
        getMappedPidByReference(getRawProductReference(product)) ||
        null;

    var directSelectionPid =
        extractPid(selectedSize) || extractPid(selectedVariation);

    var directContextPid =
        extractPid(item) ||
        extractPid(product) ||
        extractPid(eventObject);

    if (isColorGroupPid(directSelectionPid) && mappedPid) {
        return mappedPid;
    }

    if (isColorGroupPid(directSelectionPid) && referenceMappedPid) {
        return referenceMappedPid;
    }

    if (directSelectionPid) {
        return directSelectionPid;
    }

    return mappedPid || referenceMappedPid || directContextPid;
}

export function getEventQuantity (event, fallback) {
    var eventObject = event && typeof event === 'object' ? event : null;
    if (!eventObject) {
        return fallback;
    }

    var quantityValues = [
        eventObject.quantity,
        eventObject.item && eventObject.item.quantity,
        eventObject.product && eventObject.product.quantity,
    ];

    for (var i = 0; i < quantityValues.length; i++) {
        if (quantityValues[i] === null || quantityValues[i] === undefined) {
            continue;
        }

        return toNonNegativeInt(quantityValues[i], fallback);
    }

    return fallback;
}

// ---------------------------------------------------------------------------
// Remember resolved PIDs for future lookups
// ---------------------------------------------------------------------------

export function rememberResolvedPidFromEvent (event, pid) {
    var resolvedPid = trimString(pid);
    if (!resolvedPid) {
        return;
    }

    var eventObject = event && typeof event === 'object' ? event : null;
    if (!eventObject) {
        return;
    }

    var keys = [
        eventObject.productId,
        eventObject.id,
        eventObject.item && eventObject.item.id,
        eventObject.product && eventObject.product.id,
    ];

    for (var i = 0; i < keys.length; i++) {
        var key = trimString(keys[i]);
        if (key) {
            bambuserProductIdToPid[key] = resolvedPid;
        }
    }

    var references = [
        getRawProductReference(eventObject),
        getRawProductReference(eventObject.item),
        getRawProductReference(eventObject.product),
    ];

    for (var j = 0; j < references.length; j++) {
        var reference = trimString(references[j]);
        if (reference) {
            bambuserReferenceToPid[reference] = resolvedPid;
        }
    }
}
